/* eslint complexity: ["error", {max: 11}] -- a lot of conditional props and values*/
import type { GestureResponderEvent } from "react-native";

import { ModalTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import { Box, VStack } from "@/ui/layout";
import { WebModalConstraints } from "@/ui/layout/ModalTypes";
import type { ModalLayoutProps } from "@/ui/layout/ModalTypes";
import { ColorToken, LayoutToken, OpacityToken } from "@/ui/theme/tokens";

const dismissModal = () => router.dismiss();

const ModalLayout = ({
  children,
  isListView,
  isPaddedHorizontally,
  onBackdropPress = dismissModal,
  renderFloatingButton,
  renderFooter,
  renderHeader,
  scrollViewRef: _scrollViewRef,
  shouldBackdropPressDismiss,
  variant = "normal",
}: ModalLayoutProps) => {
  // on mobile, modals are full-screen.
  // on web, modals render as a card floating over a dimmed backdrop; pressing the backdrop dismisses unless a consumer opts out with shouldBackdropPressDismiss={false}.
  const handleBackdropPress =
    shouldBackdropPressDismiss === false ? undefined : onBackdropPress;
  const contentPaddingHorizontal =
    !isListView && isPaddedHorizontally
      ? LayoutToken.ModalContentPaddingHorizontal
      : 0;
  const webModalConstraints = WebModalConstraints[variant];

  return (
    <Box
      accessibilityLabel={handleBackdropPress ? "Close modal" : undefined}
      alignItems="center"
      backgroundColor={ColorToken.BackgroundDark}
      height="100%"
      justifyContent="center"
      onPress={handleBackdropPress}
      opacity={OpacityToken.Visible}
      style={{ backgroundColor: "rgba(0, 0, 0, 0.08)" }}
      testID={ModalTestID.ModalBackdrop}
    >
      <VStack
        backgroundColor={ColorToken.NeutralBackgroundPrimary}
        borderRadius={webModalConstraints.borderRadius}
        flex={1}
        maxHeight={webModalConstraints.maxHeight}
        maxWidth={webModalConstraints.maxWidth}
        // clicks inside the card must not bubble to the backdrop's dismiss handler
        onPress={(e: GestureResponderEvent) => e.stopPropagation()}
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        overflow={webModalConstraints.overflow as "hidden" | undefined}
        paddingBottom={renderFooter ? 0 : 24}
        width={webModalConstraints.width}
      >
        {renderHeader?.()}
        <Box
          display="flex"
          flex={1}
          flexDirection="column"
          minHeight={0}
          paddingLeft={contentPaddingHorizontal}
          paddingRight={contentPaddingHorizontal}
          style={{ overflowY: "auto" }}
          testID={ModalTestID.ModalLayout}
        >
          {children}
        </Box>
        {renderFloatingButton?.()}
        {renderFooter?.()}
      </VStack>
    </Box>
  );
};

export default ModalLayout;
